import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useNotify } from '../contexts/FeedbackContext';

const SelectClient = ({ orderType }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, user } = useAuth();
  const notify = useNotify();
  const orderId = searchParams.get('orderId');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestSeq = useRef(0);

  // El tipo de cliente se deriva del canal del pedido:
  // 'business' -> mayorista, 'public' -> minorista.
  const clientType = orderType === 'business' ? 'wholesale' : 'retail';
  const typeLabel = clientType === 'wholesale' ? 'Mayorista' : 'Minorista';

  useEffect(() => {
    if (user?.is_wholesale_client) {
      if (orderId) {
        navigate(`/orders/${orderId}/review`);
      } else {
        navigate('/orders/business');
      }
    }
  }, [user, navigate, orderId]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // La búsqueda se resuelve en el backend (la API pagina de a 20: filtrar en el
  // navegador dejaba afuera a los clientes que no venían en la primera página).
  useEffect(() => {
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ is_active: 'true', client_type: clientType, ordering: 'name' });
        if (searchTerm.trim()) params.set('search', searchTerm.trim());
        const response = await axios.get(`/api/clients/?${params.toString()}`);
        if (seq !== requestSeq.current) return;
        const data = response.data.results || response.data;
        setFilteredClients(data);
        setTotalCount(response.data.count ?? data.length);
      } catch (error) {
        console.error('Error fetching clients:', error);
        if (seq === requestSeq.current) notify('No se pudieron cargar los clientes. Revisá la conexión e intentá de nuevo.', 'error');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, clientType, notify]);

  const handleSelectClient = async (clientId) => {
    if (orderId) {
      // Si hay un orderId, actualizar el pedido existente
      try {
        await axios.patch(`/api/orders/${orderId}/`, {
          client_id: parseInt(clientId),
        });
        // Volver a la página de revisión del pedido
        navigate(`/orders/${orderId}/review`);
      } catch (error) {
        console.error('Error updating order:', error);
        notify(error?.response?.data?.error || 'Error al actualizar el cliente del pedido', 'error');
      }
    } else {
      // Si no hay orderId, crear un nuevo pedido según el tipo de orden
      navigate(`/orders/${orderType}?clientId=${clientId}`);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: 3, pt: { xs: 7, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
        {/* Volver al pedido del que se vino */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(orderId ? `/orders/${orderId}/review` : `/orders/${orderType}`)}
          sx={{ color: '#4e7397', textTransform: 'none', mb: 2, ml: -1 }}
        >
          {orderId ? 'Volver a la revisión del pedido' : 'Volver al pedido'}
        </Button>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' } }}>
            {orderId ? 'Cambiar cliente del pedido' : 'Elegir cliente'}
          </Typography>
          <Typography sx={{ color: '#4e7397' }}>
            Buscá el cliente {typeLabel.toLowerCase()} al que corresponde el pedido.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          {/* Left Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Search */}
            <Paper sx={{ p: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 2, color: '#0e141b' }}>
                Buscar Cliente
              </Typography>
              <TextField
                fullWidth
                placeholder="Nombre, CUIT, código o teléfono"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#4e7397' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                <Chip
                  label={`Mostrando clientes: ${typeLabel}`}
                  sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                />
              </Box>
            </Paper>

            {/* Results */}
            <Paper>
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e7edf3', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon sx={{ color: '#1976d2' }} />
                  <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>
                    Resultados de Búsqueda
                  </Typography>
                </Box>
                <Chip
                  label={totalCount > filteredClients.length
                    ? `${filteredClients.length} de ${totalCount} · afiná la búsqueda`
                    : `${totalCount} encontrados`}
                  size="small"
                  sx={{ bgcolor: '#f5f5f5', color: '#4e7397' }}
                />
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem' }}>
                          Cliente
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem', display: { xs: 'none', sm: 'table-cell' } }}>
                          Contacto
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem', display: { xs: 'none', md: 'table-cell' } }}>
                          Dirección
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem' }}>
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredClients.map((client) => (
                        <TableRow
                          key={client.id}
                          hover
                          onClick={() => handleSelectClient(client.id)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                                {getInitials(client.name)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>
                                  {client.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: '#4caf50',
                                    }}
                                  />
                                  <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                    {client.is_wholesale ? 'Mayorista' : 'Minorista'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box>
                              <Typography sx={{ fontSize: '0.875rem', color: '#0e141b' }}>
                                {client.phone || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                {client.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography sx={{ fontSize: '0.875rem', color: '#0e141b', fontWeight: 500 }}>
                              {client.delivery_address}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                              Localidad Tango: {client.delivery_city_name || client.city}
                            </Typography>
                            {client.is_wholesale && (
                              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {client.delivery_schedule?.routes?.length > 0 ? (
                                  client.delivery_schedule.routes.map((r, idx) => (
                                    <Chip
                                      key={idx}
                                      size="small"
                                      label={`Recorrido: ${r.day_label} (${r.route_name})`}
                                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                                    />
                                  ))
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Sin recorrido asignado"
                                    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); handleSelectClient(client.id); }}
                              sx={{
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  bgcolor: '#e3f2fd',
                                },
                              }}
                            >
                              Seleccionar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredClients.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography sx={{ color: '#4e7397' }}>
                              No se encontraron clientes
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>

          {/* Right Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Info */}
            <Paper sx={{ p: 2.5, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <InfoIcon sx={{ color: '#1976d2', mt: 0.5 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1565c0', mb: 0.5, fontSize: '0.875rem' }}>
                    Consejo
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#1976d2' }}>
                    Podés buscar por nombre o razón social, CUIT (con o sin guiones), código de Tango, teléfono, dirección o localidad.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SelectClient;
